import { keyframes } from "@emotion/react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCheck,
  faCopy,
  faEnvelope,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShareLinkFile } from "../../entities";
import { basenameFromPath } from "../../utils/fileFormat";

export type ReferenceFileDialogProps = {
  open: boolean;
  onClose: () => void;
  /** UID of the published space (used to build the link). */
  spaceUid: string;
  /** The file to build a "reference" deep link for. */
  file: ShareLinkFile | null;
};

/**
 * Builds the shareable deep link to a file. Mirrors {@link useSpaceBrowser}'s URL
 * scheme: a single `?target=<uid>` — the viewer opens the file's parent folder
 * and highlights the file. Uids are URL-safe, so there is nothing to encode.
 */
function buildReferenceUrl(spaceUid: string, file: ShareLinkFile): string {
  return `${window.location.origin}/share/space/${spaceUid}?target=${file.uid}`;
}

/**
 * "Reference this file" dialog: explains that the generated link takes the
 * recipient straight to the file (highlighted) inside the public space, and
 * offers two ways to share it — copy to clipboard or open a pre-filled email.
 *
 * Layout / type follow the Adobe XD spec; accent colors track the per-tenant
 * primary / primary-light.
 */
export function ReferenceFileDialog({
  open,
  onClose,
  spaceUid,
  file,
}: ReferenceFileDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // Reset the "copied" affordance whenever the dialog (re)opens for a file, and
  // auto-revert it a couple seconds after a copy.
  useEffect(() => {
    if (open) setCopied(false);
  }, [open, file]);
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(id);
  }, [copied]);

  const name = file ? basenameFromPath(file.path) || file.uid : "";
  const url = file ? buildReferenceUrl(spaceUid, file) : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard blocked (e.g. insecure context) — fall back to a prompt so the
      // link is still obtainable.
      window.prompt(t("Copy this link"), url);
    }
  };

  const handleEmail = () => {
    const subject = t('Link to "{{name}}"', { name });
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(url)}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          boxShadow: "0 0 24px rgba(17,17,26,0.16)",
          p: 0,
          // Match the viewer's user-select: none (the dialog portals out of it).
          userSelect: "none",
          // Hug the viewport on phones (smaller margins → more usable width)
          // while staying a comfortable centered card on larger screens.
          m: { xs: "16px", sm: "32px" },
          width: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
          maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
        },
      }}
    >
      <Box
        sx={{
          pt: { xs: "32px", sm: "80px" },
          pl: { xs: "24px", sm: "80px" },
          pr: { xs: "24px", sm: "60px" },
          pb: { xs: "32px", sm: "60px" },
        }}
      >
        {/* Title (XD: dialoggTitlespec). */}
        <Typography
          sx={{
            fontFamily: "IBM Plex Sans",
            fontSize: 21,
            fontWeight: 500,
            lineHeight: "26px",
            color: "#1a1a1a",
            wordBreak: "break-word",
          }}
        >
          {t('Reference "{{name}}"', { name })}
        </Typography>
        {/* Subtitle (XD: dialoggSubtitleSpec). */}
        <Typography
          sx={{
            fontFamily: "IBM Plex Sans",
            fontSize: 12,
            fontWeight: 300,
            lineHeight: "27px",
            color: "#a2a2ac",
          }}
        >
          {t("Create and share a link")}
        </Typography>

        {/* Two columns: decorative placeholder motif + explanatory text, 10px gap.
            The motif is dropped on phones so the text gets the full width. */}
        <Box
          sx={{
            mt: 3.5,
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <PlaceholderMotif />
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 15, lineHeight: "22px", color: "#1a1a1a" }}
            >
              {t(
                'This link takes the recipient directly to the file "{{name}}" in this public space and highlights it.',
                { name }
              )}
            </Typography>
            <Typography
              sx={{ fontSize: 15, lineHeight: "22px", color: "#1a1a1a" }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                {t("Please note:")}
              </Box>{" "}
              {t(
                "People with link access can navigate through all folders in the space and see their contents."
              )}
            </Typography>
          </Stack>
        </Box>

        {/* Two share options; badges straddle each card's top edge. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 4, sm: 3 }}
          sx={{ mt: { xs: 5, sm: 7 } }}
        >
          <ShareCard
            icon={faCopy}
            iconSize={21}
            succeeded={copied}
            description={t(
              "Copies the link to your clipboard to share it through any channel."
            )}
            action={
              <Button
                onClick={handleCopy}
                variant="outlined"
                startIcon={
                  copied ? (
                    <FontAwesomeIcon
                      icon={faCheck as IconProp}
                      style={{ fontSize: 13 }}
                    />
                  ) : undefined
                }
                sx={buttonSx}
              >
                {copied ? t("Link copied") : t("Copy link")}
              </Button>
            }
          />
          <ShareCard
            icon={faEnvelope}
            iconSize={24}
            description={t("Opens a new email the link can be sent from.")}
            action={
              <Button onClick={handleEmail} variant="outlined" sx={buttonSx}>
                {t("Send link via email")}
              </Button>
            }
          />
        </Stack>
      </Box>
    </Dialog>
  );
}

const buttonSx = {
  alignSelf: "center",
  borderRadius: "999px",
  textTransform: "none" as const,
  fontSize: 15,
  fontWeight: 500,
  lineHeight: "19px",
  px: 2.5,
  py: 1,
  color: "var(--primary-color)",
  borderColor: "var(--primary-light-color)",
  "&:hover": {
    borderColor: "var(--primary-light-color)",
    backgroundColor: "var(--primary-light-color)",
  },
};

// ==========================================================================
//                          Decorative placeholder motif
// ==========================================================================

/**
 * Abstract "document lines" shown left of the text: four rows of a dot + a
 * rounded bar, the third row primary-tinted. Purely decorative (hidden from
 * assistive tech).
 */
function PlaceholderMotif() {
  return (
    <Box
      aria-hidden
      sx={{
        flex: "none",
        display: { xs: "none", sm: "flex" },
        flexDirection: "column",
        gap: "8px",
        pt: "2px",
      }}
    >
      {[0, 1, 2, 3].map((i) => {
        const accent = i === 2;
        return (
          <Box
            key={i}
            sx={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                flex: "none",
                borderRadius: "50%",
                backgroundColor: accent
                  ? "var(--primary-light-color)"
                  : "#e9e9ee",
              }}
            />
            <Box
              sx={{
                width: 150,
                height: 20,
                borderRadius: "14px",
                backgroundColor: accent
                  ? "var(--primary-light-color)"
                  : "#e9e9ee",
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

// ==========================================================================
//                              Share option card
// ==========================================================================

const badgePop = keyframes`
  0%   { transform: scale(0.3); opacity: 0; }
  60%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;
const badgeRing = keyframes`
  0%   { transform: scale(0.7); opacity: 0.45; }
  100% { transform: scale(1.9); opacity: 0; }
`;

/**
 * One share option: a bordered card with a 53px icon badge straddling the middle
 * of its top edge, a left-aligned description and an action button. When
 * `succeeded` (the copy card, post-copy), the badge swaps to a checkmark with a
 * pop + an expanding ring pulse.
 */
function ShareCard({
  icon,
  iconSize,
  description,
  action,
  succeeded = false,
}: {
  icon: IconProp;
  iconSize: number;
  description: string;
  action: ReactNode;
  succeeded?: boolean;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        gap: 2,
        px: 3,
        pt: "44px",
        pb: "30px",
        borderRadius: "14px",
        border: "1px solid #DADADA",
        overflow: "visible",
      }}
    >
      {/* Icon badge — centered on the card's top edge (half above, half inside). */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 53,
          height: 53,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--primary-light-color)",
          color: "var(--primary-color)",
        }}
      >
        {succeeded && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: "2px solid var(--primary-color)",
              animation: `${badgeRing} 0.6s ease-out`,
            }}
          />
        )}
        <FontAwesomeIcon
          key={succeeded ? "check" : "icon"}
          icon={(succeeded ? faCheck : icon) as IconProp}
          style={{
            fontSize: succeeded ? 22 : iconSize,
            animation: succeeded ? `${badgePop} 0.35s ease-out` : undefined,
          }}
        />
      </Box>

      {/* Description (XD: copyLinkContainerTextSpec). */}
      <Typography
        sx={{
          fontFamily: "IBM Plex Sans",
          fontSize: 12,
          fontWeight: 300,
          lineHeight: "16px",
          color: "#1a1a1a",
        }}
      >
        {description}
      </Typography>
      {/* Action pinned to the bottom (30px via the card's padding) and centered,
          so both cards' buttons line up regardless of description length. */}
      <Box
        sx={{
          mt: "auto",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {action}
      </Box>
    </Box>
  );
}
